
import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { ClaimRequest } from './ClaimRequestInterface';

export const useClaimRequests = () => {
  const { toast } = useToast();
  const [requests, setRequests] = useState<ClaimRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  const fetchRequests = async () => {
    setIsLoading(true);
    try {
      // Using 'any' temporarily to bypass TypeScript errors until Supabase types are updated
      const { data, error } = await supabase
        .from('company_claim_requests' as any)
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      // Type assertion to convert the response to our expected type
      setRequests(data as unknown as ClaimRequest[] || []);
    } catch (error) {
      console.error('Error fetching claim requests:', error);
      toast({
        title: "Failed to load requests",
        description: "There was a problem loading the claim requests.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async (request: ClaimRequest) => {
    try {
      // Using 'any' temporarily to bypass TypeScript errors
      const { error: updateError } = await supabase
        .from('company_claim_requests' as any)
        .update({ status: 'approved' })
        .eq('id', request.id);

      if (updateError) throw updateError;

      // 2. Update user metadata to include company access
      if (request.user_id && request.company_id) {
        // This would typically be handled by an admin function in a real system
        console.log(`Would grant user ${request.user_id} access to company ${request.company_id}`);
      }
      
      toast({
        title: "Request approved",
        description: `Access has been granted to ${request.full_name} for ${request.company_name}.`,
      });
      
      // Refresh requests
      fetchRequests();
    } catch (error) {
      console.error('Error approving request:', error);
      toast({
        title: "Failed to approve request",
        description: "There was a problem processing this approval.",
        variant: "destructive"
      });
    }
  };

  const handleReject = async (request: ClaimRequest) => {
    try {
      // Using 'any' temporarily to bypass TypeScript errors
      const { error } = await supabase
        .from('company_claim_requests' as any)
        .update({ status: 'rejected' })
        .eq('id', request.id);

      if (error) throw error;
      
      toast({
        title: "Request rejected",
        description: `The request from ${request.full_name} has been rejected.`,
      });
      
      // Refresh requests
      fetchRequests();
    } catch (error) {
      console.error('Error rejecting request:', error);
      toast({
        title: "Failed to reject request",
        description: "There was a problem rejecting this request.",
        variant: "destructive"
      });
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  return {
    requests,
    isLoading,
    handleApprove,
    handleReject
  };
};
